// src/pages/CareerPage.tsx
import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Briefcase,
  Clock,
  DollarSign,
  Globe,
  Heart,
  Zap,
  ArrowRight,
  CheckCircle,
  Mail,
  Phone,
  MapPin,
  Send,
  MessageCircle,
  CheckCircle2,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Loader,
  Upload,
  Award,
  GraduationCap,
  Users,
  X,
} from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { applicationApi, showSuccess, showError } from "../lib";
import { useAuth } from "../context/AuthContext";
import SEO from "../components/SEO";

gsap.registerPlugin(ScrollTrigger);

const User = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const CareerPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [activeRole, setActiveRole] = useState<
    "instructor" | "mentor" | "team"
  >("instructor");
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formData, setFormData] = useState({
    applicationType: "mentor" as "mentor" | "instructor",
    headline: "",
    bio: "",
    expertise: "",
    yearsOfExperience: "",
    mentoringTopics: "",
    previousTeachingExperience: "",
    notableAchievements: "",
    linkedInUrl: "",
    phone: "",
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const sectionRef = useRef<HTMLElement>(null);

  const benefits = [
    {
      icon: Globe,
      title: "Remote First",
      description: "Work from anywhere in the world",
    },
    {
      icon: DollarSign,
      title: "Competitive Pay",
      description: "Industry-leading compensation",
    },
    {
      icon: Clock,
      title: "Flexible Hours",
      description: "Set your own schedule",
    },
    {
      icon: Heart,
      title: "Great Culture",
      description: "Supportive community",
    },
    {
      icon: Zap,
      title: "Growth Opportunities",
      description: "Continuous learning",
    },
    {
      icon: Award,
      title: "Recognition",
      description: "Be recognized for your work",
    },
  ];

  const roles = [
    {
      id: "instructor",
      icon: GraduationCap,
      title: "Become an Instructor",
      description:
        "Share your knowledge and expertise with thousands of students worldwide.",
      requirements: [
        "5+ years of industry experience",
        "Strong communication and teaching skills",
        "Passion for education and mentoring",
        "Portfolio of work or previous teaching experience",
      ],
      benefits: [
        "Earn up to 70% of course revenue",
        "Reach thousands of students globally",
        "Get professional course development support",
        "Build your personal brand",
      ],
    },
    {
      id: "mentor",
      icon: Users,
      title: "Become a Mentor",
      description: "Guide and support learners on their professional journey.",
      requirements: [
        "3+ years of professional experience",
        "Excellent communication and interpersonal skills",
        "Experience in mentoring or coaching",
        "Commitment to helping others grow",
      ],
      benefits: [
        "Flexible scheduling - set your own hours",
        "Competitive hourly rate",
        "Meaningful impact on students' lives",
        "Join a community of expert mentors",
      ],
    },
    {
      id: "team",
      icon: Briefcase,
      title: "Join Our Team",
      description: "Be part of a passionate team transforming education.",
      requirements: [
        "Relevant degree or equivalent experience",
        "Passion for education technology",
        "Collaborative mindset",
        "Innovative thinking",
      ],
      benefits: [
        "Competitive salary and benefits",
        "Remote work options",
        "Professional development budget",
        "Health and wellness benefits",
      ],
    },
  ];

  const faqs = [
    {
      question: "What is the application process?",
      answer:
        "Submit your application through our form. Our team reviews applications within 5-7 business days. Shortlisted candidates will be invited for an interview and a demo session.",
    },
    {
      question: "Do I need teaching experience?",
      answer:
        "For instructor roles, prior teaching or training experience is preferred. For mentor roles, we value practical industry experience and a passion for helping others learn.",
    },
    {
      question: "How much can I earn?",
      answer:
        "Instructors earn up to 70% of course revenue. Mentors earn competitive hourly rates based on experience and expertise. Full-time team members receive competitive salaries plus benefits.",
    },
    {
      question: "Is this a full-time position?",
      answer:
        "Instructor and mentor roles are flexible and can be part-time. Full-time team positions are also available for the right candidates.",
    },
    {
      question: "What support do you provide?",
      answer:
        "We provide course development support, marketing assistance, a dedicated support team, and a community of fellow instructors and mentors.",
    },
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".hero-content",
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: "expo.out",
        },
      );

      gsap.fromTo(
        ".benefit-card",
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 0.5,
          stagger: 0.08,
          ease: "expo.out",
          scrollTrigger: { trigger: ".benefits-grid", start: "top 80%" },
        },
      );

      gsap.fromTo(
        ".role-card",
        { opacity: 0, scale: 0.95 },
        {
          opacity: 1,
          scale: 1,
          duration: 0.5,
          stagger: 0.1,
          ease: "expo.out",
          scrollTrigger: { trigger: ".roles-grid", start: "top 80%" },
        },
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const handleApply = () => {
    if (!isAuthenticated) {
      navigate("/login?redirect=/careers");
      return;
    }
    setShowApplyForm(true);
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData({ ...formData, resume: e.target.files[0] });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.headline || !formData.bio || !formData.expertise || !formData.yearsOfExperience) {
      showError("Please fill in all required fields");
      return;
    }

    if (formData.bio.length < 100) {
      showError("Bio must be at least 100 characters");
      return;
    }

    setIsSubmitting(true);
    setApplicationStatus("submitting");

    try {
      const expertiseArray = formData.expertise.split(",").map((e) => e.trim()).filter(Boolean);
      const mentoringTopicsArray = formData.mentoringTopics ? formData.mentoringTopics.split(",").map((t) => t.trim()).filter(Boolean) : [];

      await applicationApi.applyForRole({
        applicationType: formData.applicationType,
        headline: formData.headline,
        bio: formData.bio,
        expertise: expertiseArray,
        yearsOfExperience: parseInt(formData.yearsOfExperience),
        mentoringTopics: mentoringTopicsArray.length > 0 ? mentoringTopicsArray : undefined,
        previousTeachingExperience: formData.previousTeachingExperience || undefined,
        notableAchievements: formData.notableAchievements || undefined,
        linkedInUrl: formData.linkedInUrl || undefined,
        phone: formData.phone || undefined,
      });

      setApplicationStatus("success");
      showSuccess("Application submitted successfully! We'll review it and get back to you soon.");
      setIsSubmitted(true);
      
      setTimeout(() => {
        setShowApplyForm(false);
        setFormData({
          applicationType: "mentor",
          headline: "",
          bio: "",
          expertise: "",
          yearsOfExperience: "",
          mentoringTopics: "",
          previousTeachingExperience: "",
          notableAchievements: "",
          linkedInUrl: "",
          phone: "",
        });
      }, 3000);
    } catch (err: any) {
      setApplicationStatus("error");
      showError(err.message || "Failed to submit application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentRole = roles.find((r) => r.id === activeRole);

  return (
    <>
      <SEO
        title="Career Opportunities - Join LearnHub Institute"
        description="Join LearnHub Institute as a mentor, instructor, or team member. Help shape the future of education while building your career with flexible hours, competitive pay, and global impact."
        keywords="careers, jobs, mentor jobs, instructor positions, edtech careers, remote work, online education jobs, work from home"
      />
      <main className="relative min-h-screen pt-20 pb-16 bg-white">
      {/* Hero Section */}
      <section className="mb-16 hero-content section-container">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary text-xs font-medium rounded-full mb-4">
            <Heart className="w-3.5 h-3.5" />
            Join Our Mission
          </span>
          <h1 className="mb-4 text-2xl font-bold text-black lg:text-3xl xl:text-4xl">
            Help Shape the Future of{" "}
            <span className="text-gradient">Education</span>
          </h1>
          <p className="max-w-3xl mx-auto text-sm leading-relaxed lg:text-base text-gray-medium">
            Join a community of passionate educators, mentors, and innovators
            dedicated to making quality education accessible to everyone,
            everywhere.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="mb-16 section-container">
        <div className="mb-8 text-center">
          <h2 className="mb-2 text-xl font-bold text-black lg:text-2xl">
            Why Join LearnHub?
          </h2>
          <p className="text-sm text-gray-medium">
            Amazing benefits for our team members
          </p>
        </div>
        <div className="grid gap-5 benefits-grid sm:grid-cols-2 lg:grid-cols-3">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="p-5 transition-all bg-white border border-gray-100 benefit-card rounded-xl shadow-card hover:shadow-card-hover hover:-translate-y-1"
            >
              <div className="flex items-center justify-center w-10 h-10 mb-3 rounded-lg bg-primary/10">
                <benefit.icon className="w-5 h-5 text-primary" />
              </div>
              <h3 className="mb-1 text-sm font-semibold text-black">
                {benefit.title}
              </h3>
              <p className="text-xs text-gray-500">{benefit.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Roles Section */}
      <section className="mb-16 section-container">
        <div className="mb-8 text-center">
          <h2 className="mb-2 text-xl font-bold text-black lg:text-2xl">
            Opportunities Available
          </h2>
          <p className="text-sm text-gray-medium">
            Find the role that matches your passion
          </p>
        </div>

        <div className="grid gap-5 mb-8 roles-grid md:grid-cols-3">
          {roles.map((role) => (
            <button
              key={role.id}
              onClick={() => setActiveRole(role.id as any)}
              className={`role-card text-left p-5 rounded-xl border transition-all duration-300 ${
                activeRole === role.id
                  ? "bg-primary text-white border-primary shadow-lg"
                  : "bg-white border-gray-200 hover:shadow-md"
              }`}
            >
              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                  activeRole === role.id ? "bg-white/20" : "bg-primary/10"
                }`}
              >
                <role.icon
                  className={`w-6 h-6 ${activeRole === role.id ? "text-white" : "text-primary"}`}
                />
              </div>
              <h3
                className={`text-base font-bold mb-2 ${activeRole === role.id ? "text-white" : "text-black"}`}
              >
                {role.title}
              </h3>
              <p
                className={`text-xs leading-relaxed ${activeRole === role.id ? "text-white/80" : "text-gray-500"}`}
              >
                {role.description}
              </p>
            </button>
          ))}
        </div>

        {currentRole && (
          <div className="p-6 bg-gray-50 rounded-xl">
            <div className="grid gap-8 md:grid-cols-2">
              <div>
                <h3 className="mb-3 text-sm font-bold text-black">
                  Requirements
                </h3>
                <ul className="space-y-2">
                  {currentRole.requirements.map((req, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-sm text-gray-600"
                    >
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      {req}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="mb-3 text-sm font-bold text-black">
                  What We Offer
                </h3>
                <ul className="space-y-2">
                  {currentRole.benefits.map((benefit, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-sm text-gray-600"
                    >
                      <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="pt-6 mt-6 text-center border-t border-gray-200">
              <button
                onClick={handleApply}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary/90 transition"
              >
                Apply Now
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </section>

      {/* FAQ Section */}
      <section className="mb-16 section-container">
        <div className="mb-8 text-center">
          <h2 className="mb-2 text-xl font-bold text-black lg:text-2xl">
            Frequently Asked Questions
          </h2>
          <p className="text-sm text-gray-medium">
            Everything you need to know
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-3">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="overflow-hidden transition-shadow bg-white border border-gray-200 rounded-xl hover:shadow-md"
            >
              <button
                onClick={() => setOpenFaq(openFaq === index ? null : index)}
                className="flex items-center justify-between w-full p-4 text-left transition-colors hover:bg-gray-50"
              >
                <span className="text-sm font-semibold text-black">
                  {faq.question}
                </span>
                {openFaq === index ? (
                  <ChevronUp className="w-4 h-4 text-gray-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                )}
              </button>
              {openFaq === index && (
                <div className="px-4 pb-4">
                  <p className="text-sm leading-relaxed text-gray-600">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Application Form Modal */}
      {showApplyForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto bg-black/50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 flex items-center justify-between p-5 bg-white border-b border-gray-200">
              <div>
                <h2 className="text-lg font-bold text-black">
                  Apply for {currentRole?.title}
                </h2>
                <p className="text-xs text-gray-500">
                  Fill out the form below to start your journey
                </p>
              </div>
              <button
                onClick={() => setShowApplyForm(false)}
                className="p-2 transition rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {isSubmitted || applicationStatus === "success" ? (
              <div className="py-12 text-center">
                <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="mb-1 text-base font-bold text-black">
                  Application Submitted!
                </h3>
                <p className="text-sm text-gray-500">
                  Thank you for your interest. Our team will review your
                  application and get back to you soon.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-5 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-black mb-1.5">
                    Application Type *
                  </label>
                  <div className="relative">
                    <Briefcase className="absolute w-4 h-4 text-gray-400 -translate-y-1/2 left-3 top-1/2" />
                    <select
                      name="applicationType"
                      value={formData.applicationType}
                      onChange={handleInputChange}
                      className="w-full py-2 pr-3 text-sm border border-gray-200 rounded-lg appearance-none pl-9 focus:outline-none focus:border-primary"
                    >
                      <option value="mentor">Mentor</option>
                      <option value="instructor">Instructor</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-black mb-1.5">
                    Headline / Job Title *
                  </label>
                  <input
                    type="text"
                    name="headline"
                    value={formData.headline}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-primary"
                    placeholder="e.g., Senior Software Engineer at Google"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-black mb-1.5">
                    Bio / Description * <span className="text-gray-400">(min 100 characters)</span>
                  </label>
                  <textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:border-primary"
                    placeholder="Tell us about your experience, background, and what makes you a great mentor..."
                  />
                  <p className="text-[10px] text-gray-400 mt-1">
                    {formData.bio.length}/100 characters
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-black mb-1.5">
                    Areas of Expertise * <span className="text-gray-400">(comma-separated)</span>
                  </label>
                  <input
                    type="text"
                    name="expertise"
                    value={formData.expertise}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-primary"
                    placeholder="e.g., React, Node.js, System Design, Career Growth"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-black mb-1.5">
                    Years of Experience *
                  </label>
                  <select
                    name="yearsOfExperience"
                    value={formData.yearsOfExperience}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-primary"
                  >
                    <option value="">Select experience</option>
                    <option value="1">1 year</option>
                    <option value="2">2 years</option>
                    <option value="3">3 years</option>
                    <option value="4">4 years</option>
                    <option value="5">5 years</option>
                    <option value="6">6 years</option>
                    <option value="7">7 years</option>
                    <option value="8">8 years</option>
                    <option value="9">9 years</option>
                    <option value="10">10+ years</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-black mb-1.5">
                    Mentoring Topics <span className="text-gray-400">(comma-separated)</span>
                  </label>
                  <input
                    type="text"
                    name="mentoringTopics"
                    value={formData.mentoringTopics}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-primary"
                    placeholder="e.g., Career Growth, Interview Prep, Technical Skills"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-black mb-1.5">
                    Previous Teaching Experience
                  </label>
                  <textarea
                    name="previousTeachingExperience"
                    value={formData.previousTeachingExperience}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:border-primary"
                    placeholder="Describe any previous teaching or mentoring experience..."
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-black mb-1.5">
                    Notable Achievements
                  </label>
                  <textarea
                    name="notableAchievements"
                    value={formData.notableAchievements}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:border-primary"
                    placeholder="Awards, certifications, key accomplishments..."
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-black mb-1.5">
                    LinkedIn Profile
                  </label>
                  <div className="relative">
                    <Linkedin className="absolute w-4 h-4 text-gray-400 -translate-y-1/2 left-3 top-1/2" />
                    <input
                      type="url"
                      name="linkedInUrl"
                      value={formData.linkedInUrl}
                      onChange={handleInputChange}
                      className="w-full py-2 pr-3 text-sm border border-gray-200 rounded-lg pl-9 focus:outline-none focus:border-primary"
                      placeholder="https://linkedin.com/in/yourprofile"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-black mb-1.5">
                    Phone Number
                  </label>
                  <div className="relative">
                    <Phone className="absolute w-4 h-4 text-gray-400 -translate-y-1/2 left-3 top-1/2" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full py-2 pr-3 text-sm border border-gray-200 rounded-lg pl-9 focus:outline-none focus:border-primary"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-2.5 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary/90 transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Application
                      <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      <style>{`
        .text-gradient {
          background: linear-gradient(135deg, #E85D04 0%, #F48C06 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>
      </main>
      </>
  );
};

export default CareerPage;
